import { getAddress, getContract } from 'viem';
import { DEAD_ADDRESS, ZERO_ADDRESS } from 'constants/index';
import useWalletContext from 'hooks/useWalletContext';

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value) {
  try {
    return getAddress(value);
  } catch {
    return false;
  }
}

export function useContract(contractAddress, ABI) {
  const { provider, signer } = useWalletContext();

  if (
    !isAddress(contractAddress) ||
    contractAddress === ZERO_ADDRESS ||
    contractAddress === DEAD_ADDRESS
  ) {
    console.warn(`Invalid 'contractAddress' parameter '${contractAddress}'.`);
    return null;
  }

  return getContract({
    address: contractAddress,
    abi: ABI,
    client: { wallet: signer, public: provider },
  });
}

export const toFixedDecimals = (value, decimals = 2) => {
  const countZeros =
    (value?.toFixed(20).toString().split('.')[1]?.match(/^0+/) || [''])[0]?.length ?? 0;

  if (countZeros === 20) {
    const numValue = Number(value);
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    const digits = countZeros + decimals;
    let regex = value
      ? value
          .toFixed(20)
          .toString()
          .match(new RegExp(`^-?\\d+(?:\.\\d{0,${digits}})?`))[0]
      : 0;
    // Convert to number and format with commas
    const numValue = Number(regex);
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    });
  }
};

export function calculateGasMargin(value) {
  return (value * 10000n + 1000n) / 10000n;
}

export function calculateGas(value) {
  return value + 1_000n; // buffer gas allowance
}

export const sleep = async (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const shortenAddress = (address, slice = false) => {
  return (
    address &&
    (slice ? address.slice(2, 8).toUpperCase() : `${address.slice(0, 6)}...${address?.slice(-4)}`)
  );
};

export const shareOnTwitterURL = ({ text, via, url }) => {
  const encodedUrl = encodeURI(url);
  return `https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}&via=${via}&original_referer=${url}`;
};

export const areEqualObjects = (first, second, options = {}) => {
  const { caseSensitive } = options;
  return (
    first.length === second.length &&
    Object.keys(first).every((firstKey) =>
      second.find((item) =>
        Object.keys(first[firstKey]).every((key) => {
          if (
            typeof item[key] == 'string' &&
            typeof first[firstKey][key] === 'string' &&
            caseSensitive
          ) {
            return item[key].toLowerCase() == first[firstKey][key].toLowerCase();
          }
          return item[key] == first[firstKey][key];
        }),
      ),
    )
  );
};

export const decimalToNumber = (decimalString) => {
  const num = Number(decimalString);
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  // Remove .00 if present
  return formatted.replace(/\.00$/, '');
};

export const decimalToNumberWithMax = (decimalString, max) => {
  const numValue = Number(decimalString);

  if (Number.isInteger(numValue)) {
    return Math.round(numValue).toLocaleString('en-US');
  }

  const formatted = numValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: max,
  });

  return formatted.replace(/\.?0+$/, '');
};

export const isZeroAddress = (address) => {
  return address === ZERO_ADDRESS;
};

export const isDeadAddress = (address) => {
  return address === DEAD_ADDRESS;
};
